import { useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import { TURN_COUNTER_FROM, TURN_MAX } from "../../lib/sse";
import { CHAT_FIELD, SendButton } from "./chat";
import { filesFromClipboard } from "../../lib/linkify";

/** Roughly eight lines; past that the field scrolls rather than eats the page. */
const MAX_HEIGHT = 208;

/**
 * `"1,801/2000"` once the limit is in sight, and nothing before that. The
 * server refuses anything longer (400 VALIDATION_ERROR); this stops first, so
 * the refusal is never how the sender finds out.
 */
export function counterFor(count: number): string | null {
  return count > TURN_COUNTER_FROM ? `${count}/${TURN_MAX}` : null;
}

type Props = {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  /** A turn is in flight: the field stays, and stays out of the way. */
  disabled: boolean;
  /** Bumps when Nob has finished speaking: put the caret back where it belongs. */
  turn: number;
  /**
   * The paperclip, left of the field (§6.3). A slot rather than a prop pair:
   * attaching needs a knock, and this composer is a shape that does not know
   * there is one.
   */
  attach?: ReactNode;
  /** The file in flight, above the field, where it does not push anything about. */
  chip?: ReactNode;
  /**
   * The mic, right of the send button. A slot for the same reason `attach` is
   * one: recording needs somewhere to send a clip, and this composer is a shape
   * that does not know there is a knock.
   */
  voice?: ReactNode;
  /**
   * The message being answered, above everything else in the composer. It sits
   * ABOVE the chip because a quote is about what is being written, and a chip
   * is about a file already moving.
   */
  quote?: ReactNode;
  /**
   * A file on the clipboard, where a file is legal.
   *
   * A screenshot is the commonest attachment there is and the commonest one to
   * lose: without this, a paste into the composer drops it silently. Absent
   * where attaching is not allowed, so a paste into a closed hearing is still
   * just a paste.
   */
  onPasteFiles?: (files: File[]) => void;
  /** Optional steps skip by sending nothing — the empty send is the answer. */
  allowEmpty?: boolean;
  /**
   * What the empty box says. The default is the intake's, because that is what
   * this composer was built for; the console's compose thread is not answering
   * a question — it is the knock — and says so instead.
   */
  placeholder?: string;
};

/**
 * The sender's half of the conversation — the composer at the foot of the
 * thread. It grows with what is typed, Enter sends it, and the one button down
 * here is the same amber send every step of the intake ended with.
 */
export function TurnInput({
  value,
  onChange,
  onSend,
  disabled,
  turn,
  attach = null,
  chip = null,
  voice = null,
  quote = null,
  onPasteFiles,
  allowEmpty = false,
  placeholder = "Answer the question.",
}: Props) {
  const filled = allowEmpty || value.trim().length > 0;
  const field = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = field.current;
    if (!el) return;
    el.style.height = "0px";
    // scrollHeight is the content box; the element is border-box, so without
    // the border the field sits 2px short and grows a phantom scrollbar.
    const border = el.offsetHeight - el.clientHeight;
    el.style.height = `${Math.min(el.scrollHeight + border, MAX_HEIGHT)}px`;
  }, [value]);

  // Nob has stopped talking; the sender is up.
  useEffect(() => {
    if (!disabled) field.current?.focus();
  }, [turn, disabled]);

  const counter = counterFor(value.length);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!disabled && filled) onSend();
      }}
    >
      {quote}
      {chip}

      <div className="flex items-end gap-2">
        {attach}
        <label htmlFor="nob-turn" className="sr-only">
          Your answer
        </label>
        <textarea
          id="nob-turn"
          ref={field}
          rows={1}
          value={value}
          maxLength={TURN_MAX}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onPaste={(e) => {
            if (!onPasteFiles) return;
            const files = filesFromClipboard(e.clipboardData);
            if (files.length === 0) return;
            // Only when it actually carried a file: a paste of text must stay
            // an ordinary paste.
            e.preventDefault();
            onPasteFiles(files);
          }}
          onKeyDown={(e) => {
            if (e.key !== "Enter" || e.shiftKey || e.altKey) return;
            e.preventDefault();
            if (!disabled && filled) onSend();
          }}
          className={`${CHAT_FIELD} resize-none overflow-y-auto`}
        />
        {voice}
        <SendButton disabled={disabled || !filled} />
      </div>

      <div className="mt-2 flex items-baseline gap-4">
        <p className="text-xs text-fainter">Enter sends. Shift and Enter for a new line.</p>
        {counter && (
          <span
            className={`ml-auto font-mono text-xs tabular-nums ${
              value.length >= TURN_MAX ? "text-accent-ink/80" : "text-faint"
            }`}
          >
            {counter}
          </span>
        )}
      </div>
    </form>
  );
}
